import type { IAPIRequestConfig, IHTTPResponse } from '@yimoka/shared';
import { isVacuous } from '@yimoka/shared';
import { createWebHttpClient, type IWebHttpClient } from '@yimoka/web-http';

import { getAppConfigId } from '@/app-config';
import { ENV } from '@/env';
import { getClientID, getLanguage, getTenantID } from '@/root';

let webHttpClient: IWebHttpClient | undefined;

function getWebHttpClient() {
  if (!webHttpClient) {
    webHttpClient = createWebHttpClient({
      baseURL: ENV.api,
      timeout: 30000,
      channel: 'web',
      useType: 'client',
      platform: typeof navigator === 'undefined' ? 'web' : navigator.platform,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      getClientID,
      getLanguage,
      getTenantID: () => getTenantID() ?? ENV.tenantID,
      getApp: getAppConfigId,
    });
  }

  return webHttpClient;
}

const httpTarget: IWebHttpClient['instance'] = Object.create(null);

export const http = new Proxy(httpTarget, {
  get: (_target, property, receiver) => Reflect.get(getWebHttpClient().instance, property, receiver),
});

function normalizeConfig<P>(config: IAPIRequestConfig<P>): IAPIRequestConfig<P> {
  const next: IAPIRequestConfig<P> = { ...config };
  const headers: Record<string, string> = { ...(next.headers ?? {}) };

  const appId = getAppConfigId();
  if (isVacuous(headers['x-md-global-app']) && appId) {
    headers['x-md-global-app'] = appId;
  }

  const tenantID = getTenantID();
  if (isVacuous(headers['x-md-global-tenantID']) && tenantID) {
    headers['x-md-global-tenantID'] = tenantID;
  }

  next.headers = headers;
  if (String(next.method).toLowerCase() === 'post' && typeof next.data === 'undefined') {
    // 旧接口 post 空 body 时需要对象，避免 content-type 被清空。
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    next.data = {} as P;
  }
  return next;
}

export const apiExecutor: IWebHttpClient['apiExecutor'] = config => getWebHttpClient().apiExecutor(normalizeConfig(config));

export const httpRequest: IHTTPRequest = <R = unknown, P = unknown>(config: IAPIRequestConfig<P>) => (
  apiExecutor(config) as Promise<IHTTPResponse<R, P>>
);

export const httpGet: IHTTPGet = (url, config) => httpRequest({ ...config, url, method: 'GET' });
export const httpDelete: IHTTPGet = (url, config) => httpRequest({ ...config, url, method: 'DELETE' });
export const httpHead: IHTTPGet = (url, config) => httpRequest({ ...config, url, method: 'HEAD' });
export const httpOptions: IHTTPGet = (url, config) => httpRequest({ ...config, url, method: 'OPTIONS' });

export const httpPost: IHTTPPost = (url, data, config) => httpRequest({ ...config, url, data, method: 'POST' });
export const httpPut: IHTTPPost = (url, data, config) => httpRequest({ ...config, url, data, method: 'PUT' });
export const httpPatch: IHTTPPost = (url, data, config) => httpRequest({ ...config, url, data, method: 'PATCH' });

export type IHTTPRequest = <R = unknown, P = unknown>(config: IAPIRequestConfig<P>) => Promise<IHTTPResponse<R, P>>;

export type IHTTPGet = <R = unknown, P = unknown>(url: string, config?: Omit<IAPIRequestConfig<P>, 'url' | 'method'>) => Promise<IHTTPResponse<R, P>>;

export type IHTTPPost = <R = unknown, P = Record<string | number | symbol, unknown>>(url: string, data?: P, config?: Omit<IAPIRequestConfig<P>, 'url' | 'method' | 'data'>) => Promise<IHTTPResponse<R, P>>;
